<template>
  <div>
    <base-dialog :active="displayLoginDialog" :title="t('common.Link Google Spreadsheet')"
      :cancel-text="t('common.Cancel')" @close="enter_inactive" @cancel="enter_inactive">
      <template #title>
        <div class="flex items-center">
          <i class="fa fa-link mr-2"></i>{{ t('common.Link Google Spreadsheet') }}
        </div>
      </template>

      <div class="p-4 space-y-4">
        <p class="mb-4">
          {{ t('sheetdialog.When you link a spreadsheet') }}
        </p>
        <p class="mb-4">
          {{ t('sheetdialog.To import sheets') }}
        </p>

        <!-- Error message display -->
        <div v-if="errorMessage" class="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border-l-4 border-red-400 rounded">
          <p class="text-sm text-red-800 dark:text-red-200">
            <i class="fas fa-exclamation-circle mr-1"></i>
            {{ errorMessage }}
          </p>
        </div>

        <!-- Additional explanation for users already signed in with Google -->
        <div v-if="isUserLoggedIn && hasGoogleLinked"
          class="mb-4 p-3 bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-400 rounded">
          <p class="text-sm text-blue-800 dark:text-blue-200">
            <i class="fas fa-info-circle mr-1"></i>
            You'll need to grant additional permissions to access your Google Sheets.
          </p>
        </div>

        <p class="text-stone-500 dark:text-stone-400 text-sm">
          {{ t('common.If you dont see a Google login button below') }}
        </p>

        <!-- New: Paste Google Sheets URL/ID when picking a sheet -->
        <div v-if="fsm === 'userIsPickingSheet'" class="mt-4 space-y-3">
          <label class="block text-sm font-medium text-stone-700 dark:text-stone-300">
            Enter Google Sheets URL or ID
          </label>
          <input type="text" v-model="sheetUrlOrId" placeholder="https://docs.google.com/spreadsheets/d/..."
            class="form-input w-full" />
          <p class="text-xs text-stone-500 dark:text-stone-400">
            We no longer use the Google Picker. Paste a Sheets link or ID, then press Continue.
          </p>
        </div>
      </div>

      <template #footer>
        <div class="flex justify-end items-center space-x-4">
          <button @click="enter_inactive" class="btn btn-secondary">
            {{ t('common.Cancel') }}
          </button>
          <button v-if="fsm === 'userIsPickingLoginMethod' || fsm === 'userIsLoggingIn'" @click="enter_userIsLoggingIn"
            :disabled="fsm === 'userIsLoggingIn'" class="btn btn-primary flex items-center space-x-2">
            <i class="fab fa-google" :class="{ 'fa-spin': fsm === 'userIsLoggingIn' }"></i>
            <span v-if="fsm === 'userIsLoggingIn'">{{ t('common.Loading') }}...</span>
            <span v-else>{{ googleButtonText }}</span>
          </button>
          <button v-else-if="fsm === 'userIsPickingSheet'" @click="confirmSelectedSheet" :disabled="!sheetUrlOrId"
            class="btn btn-primary">
            {{ t('common.Continue') || 'Continue' }}
          </button>
        </div>
      </template>
    </base-dialog>

    <base-dialog :active="displaySheetConfigDialog" :title="t('common.Link Google Spreadsheet')"
      :cancel-text="t('common.Cancel')" :confirm-text="t('sheetdialog.Link sheet')"
      :confirm-disabled="!linkSheetButtonEnabled" @close="enter_inactive" @cancel="enter_inactive"
      @confirm="enter_linkingSheet">
      <template #title>
        <div class="flex justify-between items-center w-full">
          <div class="flex items-center">
            <i class="fa fa-link mr-2"></i>{{ t('common.Link Google Spreadsheet') }}
          </div>
          <profiledropdown v-on:log-out="enter_inactive"></profiledropdown>
        </div>
      </template>

      <div class="p-4 space-y-6">
        <div class="grid grid-cols-1 md:grid-cols-3 gap-4 items-center">
          <div class="md:col-span-1">
            {{ t('sheetdialog.Selected spreadsheet') }}
          </div>
          <div class="md:col-span-2">
            <input type="text" v-model="sheetTitle" disabled class="form-input w-full bg-stone-100 dark:bg-stone-700">
          </div>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-5 gap-4 items-center">
          <div class="md:col-span-1">
            {{ t('sheetdialog.Tab') }}
          </div>
          <div class="md:col-span-4">
            <select v-model="selectedTab" class="form-select w-full">
              <option value="" disabled selected>{{ t('sheetdialog.Select a tab') }}</option>
              <option v-for="tab in tabs" :value="tab" :key="tab">
                {{ tab }}
              </option>
            </select>
          </div>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-5 gap-4 items-center">
          <div class="md:col-span-1">
            {{ t('sheetdialog.Column') }}
          </div>
          <div class="md:col-span-4">
            <select v-model="selectedColumn" class="form-select w-full" :class="{ 'opacity-50': loadingColumns }">
              <option value="" disabled selected>{{ t('sheetdialog.Select a column') }}</option>
              <option v-for="col in columns" :value="col.id" :key="col.id">
                {{ col.name }}
              </option>
            </select>
            <div v-if="loadingColumns" class="mt-2 text-sm text-stone-500 dark:text-stone-400">
              {{ t('common.Loading') }}...
            </div>
          </div>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-5 gap-4 items-center">
          <div class="md:col-span-1">
            {{ t('sheetdialog.First row') }}
          </div>
          <div class="md:col-span-4">
            <label class="flex items-center space-x-2 cursor-pointer">
              <input type="checkbox" v-model="firstRowIsHeader"
                class="h-4 w-4 text-primary-500 focus:ring-primary-500 border-stone-300 rounded">
              <span class="text-stone-700 dark:text-stone-300">
                {{ t('sheetdialog.Is a header and should not be imported') }}
              </span>
            </label>
          </div>
        </div>
      </div>
    </base-dialog>
  </div>
</template>

<script setup>
import { ref, computed, watch, getCurrentInstance, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useWheelStore } from '@/stores/useWheelStore';
import { useUserStore } from '@/stores/useUserStore';
import { getSpreadsheetTitle, getTabNames as apiGetTabNames, getValues } from '@services/sheets/sheetsApi.js';
import * as Util from '@utils/Util.js';
import profiledropdown from '../ui/profiledropdown.vue';
import BaseDialog from '../shared/BaseDialog.vue';
import { onOpen as onDialogOpen } from '@/services/ui/DialogCoordinator.js';
import useAuth from '@/composables/useAuth.js';

const { t, locale } = useI18n();
const wheelStore = useWheelStore();
const userStore = useUserStore();
const instance = getCurrentInstance();
const auth = useAuth();

const emit = defineEmits(['show-snackbar-message']);

const fsm = ref('inactive');
const sheetTitle = ref('');
const sheetId = ref('');
const tabs = ref([]);
const selectedTab = ref(null);
const columns = ref([]);
const selectedColumn = ref(null);
const loadingColumns = ref(false);
const firstRowIsHeader = ref(true);
const sheetLinkedAtMs = ref(0);
const timeoutId = ref(0);
const errorMessage = ref('');
const accessToken = ref('');
const sheetUrlOrId = ref('');

const displayLoginDialog = computed({
  get: () => fsm.value === 'userIsPickingLoginMethod',
  set: (val) => { if (val === false) fsm.value = 'inactive'; }
});

const displaySheetConfigDialog = computed({
  get: () => fsm.value === 'userIsPickingTabCol',
  set: (val) => { if (val === false) fsm.value = 'inactive'; }
});

const linkSheetButtonEnabled = computed(() => selectedTab.value && selectedColumn.value);

const isUserLoggedIn = computed(() => userStore.isUserLoggedIn);
const hasGoogleLinked = computed(() => userStore.hasGoogleLinked);
const isAuthReady = computed(() => userStore.isAuthReady);
const sheetLinked = computed(() => wheelStore.sheetLinked);
const wheelConfig = computed(() => wheelStore.wheelConfig);

const googleButtonText = computed(() => {
  if (!isUserLoggedIn.value) return t('common.Sign in with Google') || 'Sign in with Google';
  if (!hasGoogleLinked.value) return t('common.Link Google Account') || 'Link Google Account';
  return t('common.Link Google Sheet') || 'Link Google Sheet';
});

watch(selectedTab, async (newValue) => {
  if (newValue) {
    loadingColumns.value = true;
    try {
      const values = await getValues(sheetId.value, selectedTab.value, accessToken.value);
      const firstNonEmptyRow = (values || []).find((row) => Array.isArray(row) && row.length > 0) || [];
      const cols = [];
      const max = Math.min(firstNonEmptyRow.length, 27);
      for (let i = 0; i < max; i++) {
        cols.push({ id: String.fromCharCode(65 + i), name: firstNonEmptyRow[i] });
      }
      columns.value = cols;
    } catch (e) {
      console.error('Error loading columns:', e);
      columns.value = [];
    } finally {
      loadingColumns.value = false;
    }
  }
});

watch(sheetLinked, (linked) => {
  if (!linked) {
    clearTimeout(timeoutId.value);
    enter_inactive();
  }
});

const parseSheetId = (text) => {
  if (!text) return '';
  const m = String(text).match(/\/spreadsheets\/d\/([a-zA-Z0-9\-_]+)/);
  if (m && m[1]) return m[1];
  if (/^[a-zA-Z0-9\-_]{30,}$/.test(String(text))) return String(text);
  return '';
};

const confirmSelectedSheet = async () => {
  try {
    errorMessage.value = '';
    const id = parseSheetId(sheetUrlOrId.value);
    if (!id) {
      errorMessage.value = t('sheetdialog.Select a valid sheet') || 'Please enter a valid Google Sheets URL or ID.';
      return;
    }
    sheetId.value = id;
    sheetTitle.value = await getSpreadsheetTitle(sheetId.value, accessToken.value);
    enter_userIsPickingTabCol();
  } catch (ex) {
    Util.trackException(ex);
    enter_authError(ex);
  }
};

const show = async () => {
  if (!isAuthReady.value) {
    await new Promise((resolve) => {
      const unwatch = watch(
        () => userStore.isAuthReady,
        (ready) => { if (ready) { unwatch(); resolve(); } },
        { immediate: true }
      );
      setTimeout(() => { unwatch(); resolve(); }, 5000);
    });
  }
  errorMessage.value = '';
  enter_userIsPickingLoginMethod();
};

// Close if another dialog opens
let unsubscribeDialogOpen = null;
onMounted(() => {
  unsubscribeDialogOpen = onDialogOpen((name) => {
    if (name !== 'sheet') {
      enter_inactive();
    }
  });
});
onBeforeUnmount(() => {
  if (typeof unsubscribeDialogOpen === 'function') {
    try { unsubscribeDialogOpen(); } catch { }
  }
});

const enter_inactive = () => {
  setState('inactive');
  wheelStore.unlinkSheet();
};

const enter_userIsPickingLoginMethod = () => { setState('userIsPickingLoginMethod'); };

const enter_userIsLoggingIn = async () => {
  setState('userIsLoggingIn');
  errorMessage.value = '';
  try {
    Util.trackEvent('Wheel', 'LoginForSheetAttempt', '');
    const token = await auth.logInToSheets(locale.value);
    accessToken.value = token;
    await auth.postSignInSetup();
    Util.trackEvent('Wheel', 'LoginForSheetSuccess', '');
    enter_userIsPickingSheet();
  } catch (ex) {
    Util.trackException(ex);
    Util.trackEvent('Wheel', 'LoginForSheetFailure', ex.toString());
    enter_authError(ex);
  }
};

const enter_userIsPickingSheet = async () => {
  setState('userIsPickingSheet');
  sheetUrlOrId.value = '';
};

const enter_userIsPickingTabCol = async () => {
  setState('userIsPickingTabCol');
  selectedTab.value = null;
  selectedColumn.value = null;
  try {
    tabs.value = await apiGetTabNames(sheetId.value, accessToken.value);
  } catch (e) {
    console.error('Error loading tabs:', e);
    tabs.value = [];
    enter_authError(e);
  }
};

const enter_linkingSheet = async () => {
  setState('linkingSheet');
  Util.trackEvent('Wheel', 'LinkSpreadsheet', '');
  await wheelStore.setAdvanced(false);
  wheelStore.linkSheet();
  sheetLinkedAtMs.value = new Date().getTime();
  enter_readingSheet();
};

const enter_readingSheet = async () => {
  setState('readingSheet');
  try {
    const range = `${selectedTab.value}!${selectedColumn.value}:${selectedColumn.value}`;
    const values = await getValues(sheetId.value, range, accessToken.value);
    const rows = Array.isArray(values) ? values : [];
    const entries = [];
    for (let i = 0; i < rows.length; i++) {
      if (i === 0 && firstRowIsHeader.value) continue;
      const row = rows[i];
      if (Array.isArray(row) && typeof row[0] !== 'undefined') entries.push(row[0]);
    }
    const cleanedEntries = entries.map(x => Util.removeHtml(x));
    const newTextEntries = Util.getAddedTextEntries(
      wheelConfig.value.entries.map(e => e.text),
      cleanedEntries
    );
    notifyUserOfNewEntries(newTextEntries);
    wheelStore.setTextEntries(cleanedEntries);
    enter_waitingToReadSheet();
  } catch (ex) {
    Util.trackException(ex);
    Util.trackEvent(ex, 'enter_readingSheet()', navigator.userAgent);
    enter_authError(ex);
  }
};

const enter_waitingToReadSheet = () => {
  setState('waitingToReadSheet');
  timeoutId.value = setTimeout(() => {
    if (!linkHasTimedOut()) {
      enter_readingSheet();
    } else {
      enter_inactive();
    }
  }, 5000);
};

const enter_authError = (exception) => {
  setState('authError');
  console.error('Google Sheets authentication error:', exception);
  errorMessage.value = exception && exception.message ?
    `Google Sheets access failed: ${exception.message}` :
    'Failed to access Google Sheets. Please try again.';
  setState('userIsPickingLoginMethod');
};

const setState = (newState) => { fsm.value = newState; };
const linkHasTimedOut = () => {
  const MAX_LINK_TIME_MS = 3600 * 1000;
  const now = new Date().getTime();
  return ((now - sheetLinkedAtMs.value) > MAX_LINK_TIME_MS);
};
const notifyUserOfNewEntries = (newEntries) => {
  if (newEntries.length > 2) {
    const msg = t('sheetdialog.entries added', { count: newEntries.length });
    emit('show-snackbar-message', msg);
  } else {
    newEntries.forEach(entry => {
      const msg = t('sheetdialog.added', { entry: Util.removeHtml(entry) });
      emit('show-snackbar-message', msg);
    });
  }
};

defineExpose({ show, enter_inactive });
</script>
